import getPromotionalEventBrandsCount, {
  route as getPromotionalEventBrandsCountRoute,
} from "@faire/web-api/api/v2/brands/promotional-event/eventType/count/get";
import marketsAllSpecials from "@faire/web-api/routes/markets/event_type/all-specials";
import { localNum } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Insider } from "@faire/web/slate/icons/InsiderIcon";
import { HorizontalLine } from "@faire/web/slate/Layout";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { FaireMarketTheme } from "@faire/web/slate/Theme/EventsTheme";
import { getEventTheme } from "@faire/web/slate/Theme/eventsThemeColors";
import { EventsThemeLogo } from "@faire/web/slate/Theme/EventsThemeLogo";
import { EventsThemeShape } from "@faire/web/slate/Theme/EventsThemeShape";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ThemeProvider } from "@faire/web/slate/Theme/ThemeProvider";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import styled from "styled-components";

import { useMarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";

interface IProps {
  onClick?: () => void;
}

export const HomeFaireMarketHero: React.FC<IProps> = ({ onClick }) => {
  const {
    isInsiderDay,
    marketName,
    eventDaysPassed,
    eventDaysLeft,
    currentEventType,
  } = useMarketStore([
    "isInsiderDay",
    "marketName",
    "eventDaysPassed",
    "eventDaysLeft",
    "currentEventType",
  ]);
  const { data } = useQuery({
    queryKey: [getPromotionalEventBrandsCountRoute, currentEventType],
    queryFn: () => getPromotionalEventBrandsCount(currentEventType),
  });

  const getPreamble = () => {
    if (isInsiderDay) {
      return (
        <StrictLocalMsg
          defaultMessage="Insider day"
          description={{
            text: "Insider members get early access to market promotions on this day",
          }}
        />
      );
    } else if (eventDaysLeft < 1) {
      return (
        <StrictLocalMsg
          defaultMessage="Last day of the market!"
          description={{
            text: "Indicate the last day of the market event",
          }}
        />
      );
    } else if (eventDaysLeft < 2) {
      return (
        <StrictLocalMsg
          defaultMessage="Only one day left!"
          description={{
            text: "Indicate that there is only one full day left of the market event",
          }}
        />
      );
    } else {
      return (
        <StrictLocalMsg
          defaultMessage="Day {dayNumber}"
          description={{
            text: "The dayNumber day of an event (ex: Day 1)",
          }}
          values={{
            dayNumber: eventDaysPassed + 1,
          }}
        />
      );
    }
  };

  const preamble = (
    <Typography
      data-test-id="market-hero-section-preamble"
      variant="subheadingSansMedium"
      color={getEventTheme("faireMarket").promoTextInverse}
    >
      {getPreamble()}
    </Typography>
  );

  const preambleIcon = isInsiderDay ? (
    <>
      <StyledInsider />
      <Spacer size="1x" />
    </>
  ) : null;

  const title = (
    <Typography
      data-test-id="market-hero-section-title"
      variant={{
        mobileAndAbove: "displaySSerifRegular",
        tabletAndAbove: "displayLSerifRegular",
      }}
      color={getEventTheme("faireMarket").promoTextInverse}
    >
      {isInsiderDay ? (
        <StrictLocalMsg
          defaultMessage="Early access is on now."
          description={{
            text: "Indicate that Insider members have early access to the market event.",
            img: "https://cdn.faire.com/fastly/c88c6f2db6165bd61e7a1e07fa24dc1ec7d9130dd3c02fa7c34cc77219527a5f.png",
          }}
        />
      ) : (
        <StrictLocalMsg
          defaultMessage="Welcome to {marketEventName}"
          values={{
            marketEventName: marketName,
          }}
        />
      )}
    </Typography>
  );

  const subtitle = (
    <Column>
      <Typography
        data-test-id="market-hero-section-subtitle"
        variant={{
          mobileAndAbove: "paragraphSansRegular",
          tabletAndAbove: "subheadingSansRegular",
        }}
        color={getEventTheme("faireMarket").promoTextInverse}
      >
        {isInsiderDay ? (
          <StrictLocalMsg
            defaultMessage="Shop the {brandCountNumber} brands running specials and stack the deals with brands eligible for free shipping with Insider."
            description={{
              text: "Number of brands participating in the market",
              img: "https://cdn.faire.com/fastly/c88c6f2db6165bd61e7a1e07fa24dc1ec7d9130dd3c02fa7c34cc77219527a5f.png",
            }}
            values={{
              brandCountNumber: localNum(data?.count ?? 0),
            }}
          />
        ) : (
          <StrictLocalMsg
            defaultMessage="Shop {brandCountNumber} brands currently running specials"
            description={{
              text: "Number of brands participating in the market",
            }}
            values={{
              brandCountNumber: localNum(data?.count ?? 0),
            }}
          />
        )}
      </Typography>
      <Spacer size="3x" />
    </Column>
  );

  const ctaButton = (
    <ThemeProvider theme={FaireMarketTheme}>
      <StyledButton
        data-test-id="market-hero-section-cta"
        linkTo={marketsAllSpecials(currentEventType)}
        onClick={onClick}
      >
        <StrictLocalMsg
          defaultMessage="Browse participating brands"
          description={{
            text: "Call to action to browse all participating brands",
          }}
        />
      </StyledButton>
    </ThemeProvider>
  );

  return (
    <Wrapper>
      <Container>
        <Content>
          <ResponsiveContentV2
            desktopAndAbove={<StyledMarketPrimaryLogo />}
            tabletAndBelow={<StyledFaireMarketIcon />}
          />
          <TopContent>
            <Row>
              {preambleIcon}
              {preamble}
            </Row>
            <Spacer size="3x" />
            {title}
          </TopContent>
          <Spacer size="3x" />
          {data?.count ? subtitle : null}
          {ctaButton}
        </Content>
        <Spacer size="5x" />
        <HorizontalLine color={getEventTheme("faireMarket").primaryLight} />
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  background-color: ${getEventTheme("faireMarket").primaryDark};
`;

const Container = styled.div`
  max-width: 1280px;
  padding: ${getSpacing("7x", "6x", 0)};
  margin: 0 auto;

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("5x", "2x", 0)};
  }
`;

const Content = styled(Column)`
  position: relative;

  ${responsiveMediaQueries.desktopAndAbove} {
    padding-right: 444px;
  }
`;

const TopContent = styled(Column)`
  ${responsiveMediaQueries.tabletAndBelow} {
    padding-right: ${getSpacing("10x")};
  }
`;

const StyledButton = styled(Button).attrs({
  variant: "secondary",
})`
  width: fit-content;
  border: 0;

  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;

const StyledMarketPrimaryLogo = styled(
  EventsThemeLogo.faireMarket.primary
).attrs({
  isInverse: true,
  width: "220px",
  height: "177px",
})`
  position: absolute;
  top: 0;
  right: 0;
`;

const StyledFaireMarketIcon = styled(EventsThemeShape.faireMarket.badge).attrs({
  width: "64px",
})`
  position: absolute;
  top: 0;
  right: 0;
`;

const StyledInsider = styled(Insider).attrs({
  color: getEventTheme("faireMarket").promoTextInverse,
})`
  margin-top: 2px;
`;
